import {
  isPlatformAdminUser,
  isPlatformSuperAdmin,
  userHasPermission,
} from './authAccess';
import { isLimitedOrgRole, isOrgManager, isOrgOwner } from './orgAccess';

function collectOrgPermissions(user) {
  if (!user || typeof user !== 'object') return [];
  const buckets = [user.organization_permissions];
  const names = [];
  for (const bucket of buckets) {
    if (!Array.isArray(bucket)) continue;
    for (const entry of bucket) {
      if (typeof entry === 'string' && entry) names.push(entry);
      else if (entry && typeof entry === 'object' && typeof entry.name === 'string') {
        names.push(entry.name);
      }
    }
  }
  return names;
}

/** آیا کاربر tenant است و باید مجوزهای سازمان فعال را بخواند؟ */
export function usesOrganizationPermissions(user) {
  return Boolean(user) && !isPlatformSuperAdmin(user);
}

/** مجوز در سازمان فعال — برای سوپرادمین همیشه true */
export function userHasOrgPermission(user, permission) {
  if (!user || !permission) return false;
  if (isPlatformSuperAdmin(user)) return true;
  const perms = collectOrgPermissions(user);
  if (perms.includes('*')) return true;
  return perms.includes(permission);
}

/** حداقل یکی از مجوزهای سازمان فعال */
export function userHasAnyOrgPermission(user, permissions = []) {
  if (!user || !permissions?.length) return false;
  if (isPlatformSuperAdmin(user)) return true;
  return permissions.some((p) => userHasOrgPermission(user, p));
}

/** حداقل یکی از مجوزهای پلتفرمی */
export function userHasAnyPlatformPermission(user, permissions = []) {
  if (!user || !permissions?.length) return false;
  if (isPlatformSuperAdmin(user)) return true;
  return permissions.some((p) => userHasPermission(user, p));
}

/** منوی مدیریت سازمان‌های پلتفرم — سوپرادمین یا ادمین سیستم با organizations.* */
export function canAccessPlatformOrganizations(user) {
  if (isPlatformSuperAdmin(user)) return true;
  return (
    isPlatformAdminUser(user) &&
    userHasAnyPlatformPermission(user, [
      'organizations.view',
      'organizations.manage',
      'organizations.create',
      'organizations.update',
    ])
  );
}

/** دسترسی به تنظیمات سازمان فعال (workspace) */
export function canAccessWorkspace(user, organizations = null, activeOrganization = null) {
  if (!user) return false;
  if (isPlatformSuperAdmin(user)) return false;
  const orgId = activeOrganization?.id;
  if (orgId && organizations && isLimitedOrgRole(organizations, orgId)) {
    return false;
  }
  return userHasAnyOrgPermission(user, [
    'organizations.view',
    'organizations.update',
    'organizations.settings',
    'users.view',
    'departments.view',
    'roles.view',
  ]);
}

/** صندوق درخواست مشاوره فروش — سوپرادمین یا ادمین پلتفرم با مجوز پیگیری */
export function canAccessPlatformSalesConsultations(user) {
  if (!user) return false;
  if (isPlatformSuperAdmin(user)) return true;
  return isPlatformAdminUser(user) && userHasPermission(user, 'commerce.sales_requests.manage');
}

/** دسترسی به مالی و پرداخت */
export function canAccessBilling(user, organizations = null, activeOrganization = null) {
  if (!user) return false;
  if (isPlatformSuperAdmin(user)) {
    return userHasAnyOrgPermission(user, ['payments.view', 'subscriptions.view', 'wallet.view']);
  }
  if (canAccessPlatformSalesConsultations(user)) return true;
  const orgId = activeOrganization?.id;
  if (orgId && organizations && isLimitedOrgRole(organizations, orgId)) {
    return false;
  }
  return userHasAnyOrgPermission(user, ['payments.view', 'subscriptions.view', 'wallet.view']);
}

/** منوی «سایر امکانات» برای tenant */
export function canAccessTenantMoreMenu(user, organizations, activeOrganization) {
  if (!user || isPlatformSuperAdmin(user)) return true;
  const orgId = activeOrganization?.id;
  if (!orgId) return true;
  return !isLimitedOrgRole(organizations, orgId);
}

/** کاربران پلتفرمی — super_admin یا admin با users.manage */
export function canAccessPlatformUsersMenu(user) {
  if (isPlatformSuperAdmin(user)) return true;
  return isPlatformAdminUser(user) && userHasPermission(user, 'users.manage');
}

/** کاربران در سازمان فعال */
export function canAccessOrgUsersMenu(user) {
  if (isPlatformSuperAdmin(user)) return canAccessPlatformUsersMenu(user);
  return userHasOrgPermission(user, 'users.view') || userHasOrgPermission(user, 'users.manage');
}

/** منوی نقش‌های سازمان — مالک یا مجوز roles.view/manage */
export function canAccessOrgRolesMenu(user, organizations, activeOrganization) {
  if (!user || isPlatformSuperAdmin(user)) return false;
  if (!usesOrganizationPermissions(user)) return false;
  const orgId = activeOrganization?.id;
  if (!orgId) return false;
  if (isLimitedOrgRole(organizations, orgId)) return false;
  if (isOrgOwner(organizations, orgId)) return true;
  return userHasAnyOrgPermission(user, ['roles.view', 'roles.manage']);
}

/** منوی اعضای سازمان — مالک، مدیر دپارتمان، یا مجوز users.view/manage */
export function canAccessOrgMembersMenu(user, organizations, activeOrganization) {
  if (!user || isPlatformSuperAdmin(user)) return false;
  if (!usesOrganizationPermissions(user)) return false;
  const orgId = activeOrganization?.id;
  if (!orgId) return false;
  if (isLimitedOrgRole(organizations, orgId)) return false;
  if (canAccessOrgUsersMenu(user)) return true;
  return isOrgOwner(organizations, orgId) || isOrgManager(organizations, orgId);
}

/** دسترسی به بخش کاربران پلتفرم یا اعضای سازمان */
export function canAccessUsersSection(user, organizations, activeOrganization) {
  return (
    canAccessPlatformUsersMenu(user)
    || canAccessOrgMembersMenu(user, organizations, activeOrganization)
  );
}
